import React from "react";
import styles from "./Home.module.css";
import Header from "../../components/header/Header";
import PageHeading from "../../components/PageHeading/PageHeading";
import { useForm } from "react-hook-form";
import "./addCustomer.css";
import {
  useAddCustomerMutation,
  useGetCustomerDetailsQuery,
  useUpdateCustomerMutation,
} from "../../store/api/customerApi";
import { useToast } from "../../hooks/useToast";
import { useNavigate, useSearchParams } from "react-router-dom";

interface FormData {
  businessName: string;
  userEmail?: string;
  password: string;
  confirmPassword?: string;
  dbaName?: string;
  ownerName?: string;
  ownerPhone?: string;
  ownerEmail?: string;
  accountingContactName?: string;
  email: string;
  address: string;
  secondaryAddress: string;
  buildingName: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  phone?: string;
  fax?: string;
  websiteUrl?: string;
  tinGstNumber?: string;
  resaleCertificateNumber?: string;
  govLicenseNumber?: string;
  isHaveAmlProgram: boolean;
  isAcceptPolicy: boolean;
  govId: FileList | any;
  bizLicense: FileList | any;
  tradeReference?: string;
}

const CustomerDetailsForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const operation = searchParams.get("operation");
  const { data: customerDetails } = useGetCustomerDetailsQuery(Number(id), {
    skip: !id,
  });
  const [updateCustomer, { isLoading: isUpdateLoading }] =
    useUpdateCustomerMutation();
  const customerDataCamelcase = {
    businessName: customerDetails?.data?.business_name,
    userEmail: customerDetails?.data?.email,
    dbaName: customerDetails?.data?.dba_name,
    ownerName: customerDetails?.data?.owner_name,
    ownerPhone: customerDetails?.data?.owner_phone,
    ownerEmail: customerDetails?.data?.owner_email,
    accountingContactName: customerDetails?.data?.accounting_contact_name,
    email: customerDetails?.data?.email,
    address: customerDetails?.data?.address,
    secondaryAddress: customerDetails?.data?.secondary_address,
    buildingName: customerDetails?.data?.building_name,
    city: customerDetails?.data?.city,
    state: customerDetails?.data?.state,
    country: customerDetails?.data?.country,
    postalCode: customerDetails?.data?.postal_code,
    phone: customerDetails?.data?.phone,
    fax: customerDetails?.data?.fax,
    websiteUrl: customerDetails?.data?.website_url,
    tinGstNumber: customerDetails?.data?.tin_gst_number,
    resaleCertificateNumber: customerDetails?.data?.resale_certificate_number,
    govLicenseNumber: customerDetails?.data?.gov_license_number,
    // govId: customerDetails?.data?.gov_id_url,
    // bizLicense: customerDetails?.data?.biz_license_url,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    mode: "onChange",
    values: id ? (customerDataCamelcase as any) : undefined,
    defaultValues: id ? (customerDataCamelcase as any) : undefined,
  });

  const [addCustomer, { isLoading, error }] = useAddCustomerMutation();
  const validateEmail = (value: string | undefined) => {
    if (!value) return "Email is required";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value) || "Invalid email address";
  };
  const { showToast } = useToast();
  const navigate = useNavigate();

  const validatePhone = (phone: string) => {
    const re = /^[0-9]{10}$/;
    return re.test(phone) || "Phone number must be 10 digits";
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return "Invalid URL";
    }
  };

  const validatePassword = (password: string) => {
    return password.length >= 6 || "Password must be at least 6 characters";
  };

  const validateConfirmPassword = (value: string | undefined) => {
    if (!value) return "Confirm password is required";
    return value === watch("password") || "Passwords must match";
  };

  const onSubmit = async (data: FormData) => {
    let formData = data;
    delete formData.confirmPassword;
    if (!id) {
      formData.govId = formData.govId[0];
      formData.bizLicense = formData.bizLicense[0];
    }

    // Here you would typically send formData to your API
    console.log("Form data to submit:", formData);

    try {
      // Example API call:
      // const response = await fetch('your-api-endpoint', {
      //   method: 'POST',
      //   body: formData,
      // });
      // const result = await response.json();
      // console.log('Success:', result);
      let response: any;
      if (operation !== "edit") {
        response = await addCustomer(formData as any);
      } else {
        delete formData.userEmail;
        response = await updateCustomer({
          userId: Number(id),
          ...formData,
        } as any);
      }
      if (response.error) {
        console.log(response.error);
        showToast("Error submitting form", "error");
      } else {
        console.log(response.data);
        showToast(response.data?.message as string, "success");
        if (operation !== "edit") {
          navigate(
            "/add-edit-customer?id=" +
              response.data?.data?.userId +
              "&operation=add"
          );
        } else {
          navigate("/?tab=customers");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error submitting form");
    }
  };

  return (
    <div className="signup-login-form">
      <form onSubmit={handleSubmit(onSubmit)} className="p-1">
        {/* Business Information Section */}
        <fieldset>
          <div className="group-container">
            <div className="form-group">
              <label htmlFor="businessName">Business Name*</label>
              <input
                id="businessName"
                type="text"
                className={`form-input ${errors.businessName ? "error" : ""}`}
                {...register("businessName", {
                  required: "Business name is required",
                })}
              />
              {errors.businessName && (
                <span className="error-message">
                  {errors.businessName.message}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="userEmail">User Email*</label>
              <input
                id="userEmail"
                type="email"
                className={`form-input ${errors.userEmail ? "error" : ""}`}
                {...register("userEmail", {
                  required: "Email is required",
                  validate: validateEmail,
                })}
              />
              {errors.userEmail && (
                <span className="error-message">
                  {errors.userEmail.message}
                </span>
              )}
            </div>
          </div>
          {!operation && !id && (
            <div className="group-container">
              <div className="form-group">
                <label htmlFor="password">Password*</label>
                <input
                  id="password"
                  type="password"
                  className={`form-password ${errors.password ? "error" : ""}`}
                  {...register("password", {
                    required: "Password is required",
                    validate: validatePassword,
                  })}
                />
                {errors.password && (
                  <span className="error-message">
                    {errors.password.message}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password*</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className={`form-password ${
                    errors.confirmPassword ? "error" : ""
                  }`}
                  {...register("confirmPassword", {
                    required: "Confirm password is required",
                    validate: validateConfirmPassword,
                  })}
                />
                {errors.confirmPassword && (
                  <span className="error-message">
                    {errors.confirmPassword.message}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="group-container">
            <div className="form-group">
              <label htmlFor="dbaName">DBA Name</label>
              <input
                id="dbaName"
                type="text"
                className="form-input"
                {...register("dbaName")}
              />
            </div>
          </div>
        </fieldset>
        {/* Owner Information Section */}
        <fieldset>
          <div className="group-container">
            <div className="form-group">
              <label htmlFor="ownerName">Owner Name</label>
              <input
                id="ownerName"
                type="text"
                className="form-input"
                {...register("ownerName")}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ownerPhone">Owner Phone</label>
              <input
                id="ownerPhone"
                type="tel"
                className={`form-input ${errors.ownerPhone ? "error" : ""}`}
                {...register("ownerPhone", {
                  validate: (value) => !value || validatePhone(value),
                })}
              />
              {errors.ownerPhone && (
                <span className="error-message">
                  {errors.ownerPhone.message}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="ownerEmail">Owner Email</label>
              <input
                id="ownerEmail"
                type="email"
                className={`form-input ${errors.ownerEmail ? "error" : ""}`}
                {...register("ownerEmail", {
                  validate: (value) => !value || validateEmail(value),
                })}
              />
              {errors.ownerEmail && (
                <span className="error-message">
                  {errors.ownerEmail.message}
                </span>
              )}
            </div>
          </div>
        </fieldset>
        {/* Accounting Contact Section */}
        <fieldset>
          <div className="group-container">
            <div className="form-group">
              <label htmlFor="accountingContactName">Contact Name</label>
              <input
                id="accountingContactName"
                type="text"
                className="form-input"
                {...register("accountingContactName")}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email*</label>
              <input
                id="email"
                type="email"
                className={`form-input ${errors.email ? "error" : ""}`}
                {...register("email", {
                  required: "Email is required",
                  validate: validateEmail,
                })}
              />
              {errors.email && (
                <span className="error-message">{errors.email.message}</span>
              )}
            </div>
          </div>
        </fieldset>
        {/* Address Information Section */}
        <fieldset>
          <div className="group-container">
            <div className="form-group">
              <label htmlFor="address">Address*</label>
              <textarea
                id="address"
                className={`form-textarea ${errors.address ? "error" : ""}`}
                {...register("address", {
                  required: "Address is required",
                })}
              />
              {errors.address && (
                <span className="error-message">{errors.address.message}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="secondaryAddress">Secondary Address*</label>
            <textarea
              id="secondaryAddress"
              className={`form-textarea ${
                errors.secondaryAddress ? "error" : ""
              }`}
              {...register("secondaryAddress", {
                required: "Secondary address is required",
              })}
            />
            {errors.secondaryAddress && (
              <span className="error-message">
                {errors.secondaryAddress.message}
              </span>
            )}
          </div>

          <div className="group-container-grid">
            <div className="form-group">
              <label htmlFor="buildingName">Building Name*</label>
              <input
                id="buildingName"
                type="text"
                className={`form-input ${errors.buildingName ? "error" : ""}`}
                {...register("buildingName", {
                  required: "Building name is required",
                })}
              />
              {errors.buildingName && (
                <span className="error-message">
                  {errors.buildingName.message}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="city">City*</label>
              <select
                id="city"
                className={`form-select ${errors.city ? "error" : ""}`}
                {...register("city", { required: "City is required" })}
              >
                <option value="">Select City</option>
                <option value="New York">New York</option>
                <option value="Los Angeles">Los Angeles</option>
                <option value="Chicago">Chicago</option>
              </select>
              {errors.city && (
                <span className="error-message">{errors.city.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="state">State*</label>
              <select
                id="state"
                className={`form-select ${errors.state ? "error" : ""}`}
                {...register("state", { required: "State is required" })}
              >
                <option value="">Select State</option>
                <option value="CA">California</option>
                <option value="NY">New York</option>
                <option value="TX">Texas</option>
              </select>
              {errors.state && (
                <span className="error-message">{errors.state.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="country">Country*</label>
              <select
                id="country"
                className={`form-select ${errors.country ? "error" : ""}`}
                {...register("country", {
                  required: "Country is required",
                })}
              >
                <option value="">Select Country</option>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="UK">United Kingdom</option>
              </select>
              {errors.country && (
                <span className="error-message">{errors.country.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="postalCode">Postal Code*</label>
              <input
                id="postalCode"
                type="text"
                className={`form-input ${errors.postalCode ? "error" : ""}`}
                {...register("postalCode", {
                  required: "Postal code is required",
                })}
              />
              {errors.postalCode && (
                <span className="error-message">
                  {errors.postalCode.message}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                className={`form-input ${errors.phone ? "error" : ""}`}
                {...register("phone", {
                  validate: (value) => !value || validatePhone(value),
                })}
              />
              {errors.phone && (
                <span className="error-message">{errors.phone.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="fax">Fax</label>
              <input
                id="fax"
                type="tel"
                className={`form-input ${errors.fax ? "error" : ""}`}
                {...register("fax", {
                  validate: (value) => !value || validatePhone(value),
                })}
              />
              {errors.fax && (
                <span className="error-message">{errors.fax.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="websiteUrl">Website URL</label>
              <input
                id="websiteUrl"
                type="url"
                className={`form-input ${errors.websiteUrl ? "error" : ""}`}
                {...register("websiteUrl", {
                  validate: (value) => !value || validateUrl(value),
                })}
              />
              {errors.websiteUrl && (
                <span className="error-message">
                  {errors.websiteUrl.message}
                </span>
              )}
            </div>
          </div>
        </fieldset>
        {/* Business Documents Section */}
        <fieldset>
          <div className="group-container">
            <div className="form-group">
              <label htmlFor="tinGstNumber">TIN/GST Number</label>
              <input
                id="tinGstNumber"
                type="text"
                className="form-input"
                {...register("tinGstNumber")}
              />
            </div>

            <div className="form-group">
              <label htmlFor="resaleCertificateNumber">
                Resale Certificate Number
              </label>
              <input
                id="resaleCertificateNumber"
                type="text"
                className="form-input"
                {...register("resaleCertificateNumber")}
              />
            </div>
          </div>{" "}
          <div className="group-container">
            <div className="form-group">
              <label htmlFor="govLicenseNumber">
                Government License Number
              </label>
              <input
                id="govLicenseNumber"
                type="text"
                className="form-input"
                {...register("govLicenseNumber")}
              />
            </div>
          </div>
        </fieldset>
        {/* File Uploads Section */}
        {!id && (
          <fieldset>
            <div className="group-container">
              <div className="form-group">
                <label htmlFor="govId">Government ID*</label>
                <input
                  id="govId"
                  type="file"
                  className={`form-file-upload ${errors.govId ? "error" : ""}`}
                  {...register("govId", {
                    required: "Government ID is required",
                  })}
                />
                {errors.govId && (
                  <span className="error-message">
                    {errors.govId?.message as string}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="bizLicense">Business License*</label>
                <input
                  id="bizLicense"
                  type="file"
                  className={`form-file-upload ${
                    errors.bizLicense ? "error" : ""
                  }`}
                  {...register("bizLicense", {
                    required: "Business license is required",
                  })}
                />
                {errors.bizLicense && (
                  <span className="error-message">
                    {errors.bizLicense?.message as string}
                  </span>
                )}
              </div>
            </div>
          </fieldset>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={operation === "add" || isLoading || isUpdateLoading}
          >
            {isLoading || isUpdateLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerDetailsForm;
